(() => {
  const N = window.Northyard = window.Northyard || {};
  if (N.ProductState) return;
  const pending = new Map();
  const cache = new Map();
  const TTL = 10000;
  const isColor = (name) => /colou?r/i.test(name);
  const imageAlt = (item, title = '') => {
    if (typeof item?.display_alt === 'string') return item.display_alt;
    const raw = String(item?.alt || title).trim();
    if (!/(?:#color_|(?:^|\|)\s*(?:colou?r|view|display|length|inseam)\s*:)/i.test(raw)) return raw;
    const parts = raw.split('|').map(part => part.trim()).filter(part => !/^display\s*:/i.test(part))
      .map(part => part.replace(/^.*?#color_/i, '').replace(/^(?:colou?r|view|length|inseam)\s*:\s*/i, '').trim())
      .filter(part => part && part !== title);
    return [title, ...parts].filter(Boolean).join(' — ');
  };
  const selectedIds = (payload, selected = {}) => payload.options.map((option) => {
    const value = option.values.find((entry) => entry.label === selected[option.name])
      || option.values.find((entry) => entry.selected);
    return value ? String(value.id) : '';
  });
  const selectionUrl = (source, { optionValues, variantId } = {}) => {
    const url = new URL(source, location.origin);
    if (url.origin !== location.origin) throw new Error('Invalid product origin.');
    url.searchParams.delete('view');
    url.searchParams.delete('section_id');
    url.searchParams.delete('sections');
    if (optionValues) {
      url.searchParams.delete('variant');
      url.searchParams.set('option_values', optionValues.join(','));
    } else if (variantId) {
      url.searchParams.delete('option_values');
      url.searchParams.set('variant', String(variantId));
    }
    return url;
  };
  const request = (source, options = {}) => {
    const url = selectionUrl(source, options);
    url.searchParams.set('section_id', 'product-state');
    const preview = new URL(location.href).searchParams.get('preview_theme_id');
    if (preview) url.searchParams.set('preview_theme_id', preview);
    const key = url.href + '|' + document.documentElement.lang + '|' + (window.Shopify?.currency?.active || '');
    const existing = cache.get(key);
    if (!options.fresh && existing && Date.now() - existing.time < TTL) return Promise.resolve(existing.value);
    if (pending.has(key)) return pending.get(key);
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 15000);
    const promise = fetch(url.href, { headers: { Accept: 'text/html' }, credentials: 'same-origin', cache: 'no-store', signal: controller.signal })
      .then(async (response) => {
        if (!response.ok) throw new Error('Product selection could not be loaded.');
        const document = new DOMParser().parseFromString(await response.text(), 'text/html');
        const node = document.querySelector('[data-product-state]');
        if (!node) throw new Error('Product state is missing.');
        const value = JSON.parse(node.textContent);
        if (value.version !== 1 || !value.product?.id || !Array.isArray(value.options)) throw new Error('Invalid product state.');
        if (options.optionValues && selectedIds(value).join(',') !== options.optionValues.map(String).join(',')) throw new Error('Selection was not preserved by the server.');
        if (options.variantId && String(value.variant?.id) !== String(options.variantId)) throw new Error('Variant was not resolved by the server.');
        cache.set(key, { time: Date.now(), value });
        while (cache.size > 32) cache.delete(cache.keys().next().value);
        return value;
      }).finally(() => { clearTimeout(timer); pending.delete(key); });
    pending.set(key, promise);
    return promise;
  };
  const matches = (variant, payload, selected) => Boolean(variant) && payload.options.every((option) => (
    !selected[option.name] || variant.options[option.position - 1] === selected[option.name]
  ));
  const exact = (payload, selected) => payload.options.every((option) => selected[option.name])
    && matches(payload.variant, payload, selected) ? payload.variant : null;
  const representative = (payload, selected) => {
    const candidates = [payload.variant, ...(payload.adjacent_variants || [])].filter((variant) => matches(variant, payload, selected));
    const available = candidates.filter((variant) => variant.available);
    return (available.length ? available : candidates).sort((a, b) => a.price - b.price)[0] || null;
  };
  const card = async (source, options = {}) => {
    const first = await request(source, options);
    const color = first.options.find((option) => isColor(option.name));
    const remainder = first.options.filter((option) => option.position !== first.adjacent_position && option !== color);
    const states = [first];
    const ids = selectedIds(first);
    const work = remainder.flatMap((option) => option.values.filter((value) => !value.selected).map((value) => {
      const next = [...ids];
      next[option.position - 1] = String(value.id);
      return next;
    }));
    let index = 0;
    await Promise.all(Array.from({ length: Math.min(3, work.length) }, async () => {
      while (index < work.length) {
        const optionValues = work[index++];
        states.push(await request(source, { optionValues }));
      }
    }));
    const variants = [...new Map(states.flatMap((value) => [value.variant, ...value.adjacent_variants]).filter(Boolean).map((variant) => [variant.id, variant])).values()];
    const available = variants.filter((variant) => variant.available);
    const candidates = available.length ? available : variants;
    candidates.sort((a, b) => a.price - b.price);
    const variant = candidates[0] || null;
    const matchingState = states.find((value) => value.variant?.id === variant?.id || value.adjacent_variants.some((entry) => entry.id === variant?.id)) || first;
    return { payload: matchingState, variant, available: available.length > 0,
      varies: available.length > 1 && available[0].price !== available[available.length - 1].price };
  };
  const prepareImage = async (image, { signal, onSlow } = {}) => {
    if (!image) return;
    if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');
    image.loading = 'eager';
    if (image.complete && image.naturalWidth) return;
    await new Promise((resolve, reject) => {
      const finish = (error) => {
        clearTimeout(timer);
        signal?.removeEventListener('abort', abort);
        if (error) reject(error); else resolve();
      };
      const abort = () => {
        finish(new DOMException('Aborted', 'AbortError'));
        if (!image.isConnected) { image.removeAttribute('srcset'); image.removeAttribute('src'); }
      };
      const timer = setTimeout(() => {
        if (onSlow) onSlow();
        else finish(new Error('Image loading timed out.'));
      }, 12000);
      signal?.addEventListener('abort', abort, { once: true });
      image.decode().then(() => finish(), finish);
    });
  };
  const alternativeColors = (payload, selected) => {
    const variant = exact(payload, selected);
    if (!variant || variant.available !== false) return [];
    const color = payload.options.find(option => isColor(option.name));
    return (payload.color_alternatives || []).filter(item => color && item.position === color.position
      && item.variant?.available === true && item.variant.options?.length === variant.options.length
      && item.variant.options[color.position - 1] === item.label
      && item.label !== selected[color.name]
      && variant.options.every((value, index) => index === color.position - 1 || value === item.variant.options[index])).slice(0, 6);
  };
  const alternativeSelection = (payload, selected, variantId) => {
    const item = alternativeColors(payload, selected).find(entry => String(entry.variant.id) === String(variantId));
    if (!item) return null;
    const option = payload.options.find(entry => entry.position === item.position);
    const value = option?.values.find(entry => entry.label === item.label);
    if (!value || value.product_url) return null;
    return { name: option.name, value: value.label };
  };
  const alternativesMarkup = (payload, selected, source, labels) => {
    const variant = exact(payload, selected);
    if (!variant || variant.available !== false) return '';
    const safeUrl = (url, variantId) => {
      try {
        const target = selectionUrl(url, variantId ? {variantId} : {});
        const preview = new URL(location.href).searchParams.get('preview_theme_id');
        if (preview) target.searchParams.set('preview_theme_id', preview);
        if (!target.pathname.includes('/products/')) return '';
        return target.pathname + target.search;
      } catch (_) { return ''; }
    };
    const colors = alternativeColors(payload, selected);
    const links = colors.map(item => {
      const url = safeUrl(source || payload.url, item.variant.id);
      return url ? '<li><a data-stock-color="' + N.html(item.variant.id) + '" href="' + N.html(url) + '">' + N.html(item.label) + '</a></li>' : '';
    }).join('');
    const related = (payload.related_alternatives || []).slice(0, 3).map(item => {
      const url = safeUrl(item.url);
      return url ? '<li><a href="' + N.html(url) + '">' + N.html(item.title) + ' — ' + N.html(labels.labelCheckSizes) + '</a></li>' : '';
    }).join('');
    if (!links && !related) return '';
    return '<section class="stock-alternatives">' + (links ? '<p>' + N.html(labels.labelOtherColors) + '</p><ul>' + links + '</ul>' : '')
      + (related ? '<div class="stock-alternatives__related"><p>' + N.html(labels.labelSimilarStyles) + '</p><ul>' + related + '</ul></div>' : '') + '</section>';
  };
  N.ProductState = { request, selectedIds, selectionUrl, exact, representative, card, prepareImage, alternativesMarkup, alternativeSelection, imageAlt };
})();
